import React, { useEffect, useState, useMemo } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Checkbox from "../../common/Checkbox";
import SwitchStatus from "../../common/Status";
import Pagination from "../../common/TablePagination";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import {
  getAllTargets,
  getFundraisers,
  toggleFundraiserStatus,
  deleteFundraiserById,
  approveOrRejectFundraiser,
} from "../../../services/fundraiserService";
import RejectOfferPopup from "../../common/RejectOfferPopup";
import crossIcon from "../../../Assets/Icons/cross.svg";
import closeIcon from "../../../Assets/Icons/popupCloseIcon.svg";
import whiteTick from "../../../Assets/Icons/White Tick.svg";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedWhiteBox.svg";
import { ReactComponent as UncheckedGreyIcon } from "../../../Assets/Icons/unCheckedGreyIcon.svg";
import { ReactComponent as ApprovedIcon } from "../../../Assets/Icons/approveIcon.svg";
import { ReactComponent as DeclinedIcon } from "../../../Assets/Icons/declinedIcon.svg";
import { ReactComponent as PendingIcon } from "../../../Assets/Icons/pendingIcon.svg";
import "./fundraiserList.scss";
import SearchBar from "../../common/SearchBar";
import { Dropdown } from "antd";
import { toast } from "react-toastify";
import FilterPanel from "../../common/FilterPanel";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import debounce from "lodash.debounce";
import { getFundraiserById } from "../../../services/fundraiserService";
import DetailsModal from "../../common/DetailsModal";
import PopupContent from "./PopupContent";
import CancelPopup from "../../common/CancelPopup";
import deleteImg from "../../../Assets/Icons/deleteIcon.svg";

const FundraiserListDataTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [allFundraisers, setAllFundraisers] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [statusToggled, setStatusToggled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
  });
  const [fundraiserDetails, setFundraiserDetails] = useState({});
  const [selectedYear, setSelectedYear] = useState("2025");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fundraiserToDelete, setFundraiserToDelete] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const getFundraisersData = async (search) => {
    const params = {
      page: currentPage,
      search: search || null,
      status: filterParams.status?.toLowerCase() || null,
      duration: filterParams.duration?.toLowerCase() || null,
    };

    // Remove null values to avoid sending empty parameters
    Object.keys(params).forEach((key) =>
      params[key] === null ? delete params[key] : {}
    );

    const fundraisersPromise = await dispatch(getFundraisers(params))?.unwrap();
    if (fundraisersPromise?.success === true) {
      setAllFundraisers(fundraisersPromise?.data);
      setCurrentPage(fundraisersPromise?.currentPage);
      setTotalPages(fundraisersPromise?.totalPages);
      setCount(fundraisersPromise?.count);
    }
  };

  useEffect(() => {
    getFundraisersData(searchQuery);
  }, [filterParams, currentPage, statusToggled]);

  const items = [
    {
      title: "Fundraisers",
    },
    {
      title: "Fundraiser List",
    },
  ];

  const columns = [
    {
      name: "",
      width: "40px",
    },
    {
      name: "No.",
    },
    {
      name: "Name",
    },
    {
      name: "Date Joined",
    },
    {
      name: "Email",
    },
    {
      name: "Contact",
    },
    {
      name: "Balance",
    },
    {
      name: "Status",
    },
    {
      name: "Actions",
    },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of fundraiser list when changing pages
    const container = document.getElementById('fundraiser-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Allow selecting all fundraisers (any status) to enable moving Approved/Declined back to Pending
  const getSelectableFundraisers = () => {
    return allFundraisers || [];
  };

  const handleSelectAll = () => {
    const selectableFundraisers = getSelectableFundraisers();

    if (
      selectableFundraisers.length > 0 &&
      selected.length === selectableFundraisers.length
    ) {
      setSelected([]);
    } else {
      const newSelected = selectableFundraisers.map(
        (fundraiser) => fundraiser._id
      );
      setSelected(newSelected);
    }
  };

  const handleSelectOne = (event, id, status) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // All statuses selectable
  const isSelectable = (status) => {
    return true;
  };

  const handleBulkApprove = async () => {
    try {
      const payload = { ids: selected, status: "approved" };
      const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
      if (res?.success === true) {
        setSelected([]);
        setShowActionModal(false);
        setStatusToggled(!statusToggled);
        try { toast.success("Fundraiser(s) approved successfully"); } catch (_) { }
      }
    } catch (_) { }
  };

  const handleBulkDecline = async () => {
    // Open modal to capture reason, then submit via submitBulkDecline
    setBulkDeclineReason("");
    setShowBulkDeclineModal(true);
  };

  // Move selected fundraisers to pending
  const handleBulkPending = async () => {
    try {
      const payload = { ids: selected, status: "pending" };
      const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
      if (res?.success === true) {
        setSelected([]);
        setShowActionModal(false);
        setStatusToggled(!statusToggled);
        try { toast.success("Moved to pending"); } catch (_) { }
      }
    } catch (_) { }
  };

  // --- Bulk Decline Modal State/Handlers ---
  const [showBulkDeclineModal, setShowBulkDeclineModal] = useState(false);
  const [bulkDeclineReason, setBulkDeclineReason] = useState("");

  const closeBulkDeclineModal = () => {
    setShowBulkDeclineModal(false);
    setBulkDeclineReason("");
  };

  const submitBulkDecline = async () => {
    const trimmed = (bulkDeclineReason || "").trim();
    if (!trimmed || selected.length === 0) return;
    try {
      const payload = { ids: selected, status: "declined", reason: trimmed };
      const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
      if (res?.success) {
        setSelected([]);
        setShowActionModal(false);
        setStatusToggled(!statusToggled);
        closeBulkDeclineModal();
        try { toast.success("Fundraiser(s) declined successfully"); } catch (_) { }
      }
    } catch (_) { }
  };

  useEffect(() => {
    if (selected.length > 0) {
      setShowActionModal(true);
    } else {
      setShowActionModal(false);
    }
  }, [selected]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  const [filterData, setFilterData] = useState([
    {
      id: "duration",
      title: "Duration",
      options: ["Day", "Week", "Month", "Year"],
      defaultValue: "",
      multiSelect: false,
    },
    {
      id: "status",
      title: "Status",
      options: ["Approved", "Declined", "Pending"],
      defaultValue: "",
      multiSelect: false,
    },
  ]);

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        // For multi-select, initialize as array
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        // For single select, initialize as single value
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // Initialize selections with the initial state
  const [selections, setSelections] = useState(createInitialState(filterData));

  const handleSelectionChange = (filterId, value) => {
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  // Update the handleReset function
  const handleReset = () => {
    const initialState = createInitialState(filterData);
    setSelections(initialState);

    // Reset filter params to trigger new API call
    setFilterParams({
      status: null,
      duration: null,
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        getFundraisersData(query);
      }, 500),
    [filterParams]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleApply = () => {
    console.log("Applying filters:", selections);

    // Update filter params to trigger new API call
    setFilterParams({
      status: selections.status,
      duration: selections.duration,
    });

    setOpen(false);
  };

  const fetchFundraiserDetails = async (fundraiserId) => {
    try {
      const fundraiserDetailsPromise = await dispatch(
        getFundraiserById({ id: fundraiserId })
      )?.unwrap();
      if (fundraiserDetailsPromise?.success === true) {
        setFundraiserDetails(fundraiserDetailsPromise?.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const fetchAllTargets = async (fundraiserId, year) => {
    try {
      const targetsPromise = await dispatch(
        getAllTargets({ id: fundraiserId, year })
      )?.unwrap();
      if (targetsPromise?.success === true) {
        setFundraiserDetails((prevDetails) => ({
          ...prevDetails,
          target: targetsPromise?.data,
        }));
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const openPopup = async (fundraiserId) => {
    try {
      const [fundraiserDetailsPromise, targetsPromise] = await Promise.all([
        dispatch(getFundraiserById({ id: fundraiserId }))?.unwrap(),
        dispatch(
          getAllTargets({ id: fundraiserId, year: selectedYear })
        )?.unwrap(),
      ]);

      if (fundraiserDetailsPromise?.success && targetsPromise?.success) {
        setFundraiserDetails({
          ...fundraiserDetailsPromise.data,
          target: targetsPromise.data,
        });
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching fundraiser or target details:", error);
    }
  };

  const closePopup = () => {
    setSelectedYear("2025");
    setFundraiserDetails({});
    setIsDetailOpen(false);
  };

  const handleYearChange = (fundraiserId, dateString) => {
    console.log("Selected Year for API:", dateString);
    // Update the state with the new year
    setSelectedYear(dateString);
    // Fetch targets with the new year
    fetchAllTargets(fundraiserId, dateString);
  };

  const handleDeleteFundraiser = (id) => {
    setFundraiserToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const deletePromise = await dispatch(
        deleteFundraiserById({ id: fundraiserToDelete })
      )?.unwrap();
      if (deletePromise?.success === true) {
        setIsDetailOpen(false);
        getFundraisersData(searchQuery);
      }
    } catch (error) {
      console.error("Error deleting fundraiser:", error);
    } finally {
      setShowDeleteConfirm(false);
      setFundraiserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setFundraiserToDelete(null);
  };

  const handleShowReviewModal = () => {
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
  };

  // Decline flow using themed modal and closing details first
  const [showFundraiserDecline, setShowFundraiserDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineTargetId, setDeclineTargetId] = useState(null);

  const openDeclineModal = (fundraiserId) => {
    setIsDetailOpen(false); // hide details while decline modal is open
    setDeclineTargetId(fundraiserId);
    setDeclineReason("");
    setShowFundraiserDecline(true);
  };

  const closeDeclineModal = () => {
    setShowFundraiserDecline(false);
    setDeclineReason("");
    setDeclineTargetId(null);
    // Reopen the details modal when closing decline modal
    setIsDetailOpen(true);
  };

  const submitDecline = async () => {
    const trimmed = (declineReason || "").trim();
    if (!trimmed || !declineTargetId) return;
    try {
      const payload = { ids: [declineTargetId], status: "declined", reason: trimmed };
      const resp = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
      if (resp?.success) {
        setFundraiserDetails(prev => ({ ...(prev || {}), status: 'declined' }));
        setStatusToggled(!statusToggled);
        closeDeclineModal();
      }
    } catch (_) { }
  };

  const handleReviewStatus = async (fundraiserId, newStatus) => {
    try {
      const payload = { ids: [fundraiserId], status: newStatus };
      const statusPromise = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        setIsDetailOpen(false);
        setShowReviewModal(false);
      }
    } catch (e) {
      console.error("Error reviewing fundraiser status:", e);
    }
  };

  // Calculate count of selectable items for "Select All" checkbox
  const selectableCount = getSelectableFundraisers().length;
  const allSelectableSelected =
    selectableCount > 0 && selected.length === selectableCount;

  // Determine which bulk action buttons to show based on selected statuses
  const selectedStatuses = selected
    .map((id) => allFundraisers?.find((f) => f._id === id)?.status)
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const hasPendingSelected = selectedStatuses.includes("pending");
  const hasApprovedDeclinedSelected = selectedStatuses.some(status => status === "approved" || status === "declined");

  // Show approve/decline buttons only if there are pending items selected
  const showApproveDecline = selected.length > 0 && hasPendingSelected;
  // Show move to pending only if there are approved/declined items selected
  const showMoveToPending = selected.length > 0 && hasApprovedDeclinedSelected;

  return (
    <>
      {loading === true && <Loader />}
      {isDetailOpen && (
        <DetailsModal
          handleClose={closePopup}
          content={
            <PopupContent
              merchantDetails={fundraiserDetails}
              setSelectedYear={setSelectedYear}
              selectedYear={selectedYear}
              handleYearChange={handleYearChange}
              onDelete={handleDeleteFundraiser}
              onShowReviewModal={handleShowReviewModal}
              onDecline={openDeclineModal}
              onStatusChange={(newStatus) => setFundraiserDetails(prev => ({ ...(prev || {}), status: newStatus }))}
            />
          }
          popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
        />
      )}
      {showFundraiserDecline && (
        <RejectOfferPopup
          handleClose={closeDeclineModal}
          content={"Please provide a reason for declining this fundraiser. Your reason will be emailed to the fundraiser contact."}
          popupClassName={"w-[335px] sm:w-[470px] pb-4"}
          setReason={setDeclineReason}
          handleOfferRejection={submitDecline}
          showCloseIcon={true}
        />
      )}
      {showDeleteConfirm && (
        <CancelPopup
          handleNo={handleDeleteCancel}
          handleYes={handleDeleteConfirm}
          heading="Delete Fundraiser"
          content="Are you sure you want to delete this fundraiser profile? This action cannot be undone."
          image={deleteImg}
          confirmation={false}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      <div className="flex justify-between col-span-12">
        <BreadCrumsHeader heading="Fundraiser List" items={items} />
      </div>

      <div className="w-full flex justify-between items-center mt-4">
        <SearchBar
          parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative"
          inputClassName="searchBar w-full p-3 text-[14px] font-poppins font-[400] outline-none"
          iconClassName="absolute sm:right-[5%] sm:bottom-[25%] lg:right-[3%]"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: "320px",
            maxHeight: "80vh",
            zIndex: 1000,
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          <button className="flex justify-center items-center gap-2 border border-[#00ADEF] rounded-md p-3 text-lg font-medium outline-none h-[40px]">
            <div className="flex items-center gap-2">
              <FiltersIcon className="w-[20px] h-[20px]" />
              <span className="font-[500] text-[14px] font-poppins leading-[22px]">
                Filters
              </span>
            </div>
            <DownArrow />
          </button>
        </Dropdown>
      </div>

      <div id="fundraiser-list-container" className="col-span-12 mt-4 relative">
        <TableContainer style={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                <TableCell className="!border-b-0">
                  <Checkbox
                    checked={allSelectableSelected}
                    onChecked={handleSelectAll}
                    divClassName="flex items-center"
                    UncheckedIcon={UncheckedIcon}
                    checkClassName={"!w-[22px] !h-[22px]"}
                  />
                </TableCell>
                {columns.slice(1).map((column) => {
                  return (
                    <TableCell key={column.name} className="!text-[#FFFFFF]">
                      {column.name}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allFundraisers?.map((fundraiser, index) => {
                const isItemSelected = isSelected(fundraiser._id);
                const itemSelectable = isSelectable(fundraiser.status);

                return (
                  <TableRow
                    key={fundraiser._id}
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isItemSelected}
                        onChecked={(event) =>
                          handleSelectOne(
                            event,
                            fundraiser._id,
                            fundraiser.status
                          )
                        }
                        divClassName={`flex items-center ${!itemSelectable
                            ? "opacity-50 !cursor-not-allowed"
                            : ""
                          }`}
                        checkClassName={`!w-[22px] !h-[22px] ${!itemSelectable
                            ? "opacity-50 !cursor-not-allowed"
                            : ""
                          }`}
                        disabled={!itemSelectable}
                      />
                    </TableCell>
                    <TableCell>{(currentPage - 1) * 50 + index + 1}</TableCell>
                    <TableCell>{fundraiser?.user?.name}</TableCell>
                    <TableCell>
                      {
                        separateDateTimeAndConvertToLocale(
                          fundraiser?.createdAt
                        )?.localDate
                      }
                    </TableCell>
                    <TableCell>{fundraiser?.user?.email}</TableCell>
                    <TableCell>{fundraiser?.contactPersonNumber}</TableCell>
                    <TableCell>{"$" + fundraiser?.balance}</TableCell>
                    <TableCell>
                      {fundraiser?.status == "pending" ? (
                        <PendingIcon />
                      ) : fundraiser?.status == "approved" ? (
                        <ApprovedIcon />
                      ) : (
                        <DeclinedIcon />
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        className={`flex justify-center items-center border rounded-[6px] p-3 h-[34px] border-[#00ADEF] text-[#00ADEF]`}
                        onClick={() => openPopup(fundraiser?._id)}
                      >
                        <span className="font-[600] text-[14px] font-poppins leading-[22px]">
                          View Details
                        </span>
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />

        {/* Action Modal */}
        {showActionModal && (
          <div
            className="fixed bottom-16 left-[57%] transform -translate-x-1/2 bg-white rounded-[6px] px-4 h-[60px] flex items-center gap-4 z-50 text-[14px] font-[600] text-[#303030] border border-[#0000001A]"
            style={{ boxShadow: "0px 4px 20px 0px #00000029" }}
          >
            <div className="flex items-center gap-2">
              <span className="!text-[12px] !font-[500] !text-[#000000] border border-[#2A35484D] rounded-[4px] w-[24px] h-[24px] flex items-center justify-center">
                {selected.length}
              </span>
              <span className="!font-[500]">Selected</span>
            </div>
            <div className="flex items-center gap-16">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelectableSelected}
                    onChecked={handleSelectAll}
                    divClassName="flex items-center"
                    UncheckedIcon={UncheckedGreyIcon}
                    checkClassName={"!w-[22px] !h-[22px]"}
                  />
                  <span className="whitespace-nowrap !font-[500]">
                    Select All
                  </span>
                </div>
                {showApproveDecline && (
                  <>
                    <button
                      onClick={handleBulkApprove}
                      className={`bg-[#00ADEF] text-white h-[38px] whitespace-nowrap pl-4 pr-8 py-1 rounded-[6px] flex items-center gap-2 ${selected.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={selected.length === 0}
                    >
                      <img
                        src={whiteTick}
                        alt="tick"
                        className="h-[16px] w-[16px]"
                      />
                      {selected.length > 1 ? 'Approve Fundraisers' : 'Approve Fundraiser'}
                    </button>
                    <button
                      onClick={handleBulkDecline}
                      className={`border border-[#00ADEF] text-[#00ADEF] h-[38px] px-4 py-1 rounded-[6px] whitespace-nowrap ${selected.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={selected.length === 0}
                    >
                      {selected.length > 1 ? 'Decline Fundraisers' : 'Decline Fundraiser'}
                    </button>
                  </>
                )}
                {showMoveToPending && (
                  <button
                    onClick={handleBulkPending}
                    className="border border-[#00ADEF] text-[#00ADEF] h-[38px] px-4 py-1 rounded-[6px] whitespace-nowrap"
                  >
                    Move to Pending
                  </button>
                )}
              </div>
              <button
                onClick={() => setSelected([])}
                className="w-[10px] h-[10px]"
              >
                <img src={closeIcon} alt="Close" className="w-full h-full" />
              </button>
            </div>
          </div>
        )}

        {/* Review Status Modal */}
        {showReviewModal && (
          <div className="popup-container" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl border border-[#00ADEF]">
              <h3 className="text-lg font-semibold mb-4 text-center">Review Status</h3>
              <p className="text-gray-600 mb-6 text-center">
                Are you sure you want to change the status from{" "}
                <span className="font-semibold capitalize">{fundraiserDetails?.status}</span> to{" "}
                <span className="font-semibold">Pending</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseReviewModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReviewStatus(fundraiserDetails?._id, "pending")}
                  className="flex-1 px-4 py-2 bg-[#00ADEF] text-white rounded-md hover:bg-[#0088CC]"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {showBulkDeclineModal && (
          <RejectOfferPopup
            handleClose={closeBulkDeclineModal}
            content={"Please provide a reason for declining the selected fundraiser(s)."}
            popupClassName={"w-[335px] sm:w-[470px] pb-4"}
            setReason={setBulkDeclineReason}
            handleOfferRejection={submitBulkDecline}
            showCloseIcon={true}
          />
        )}
      </div>
    </>
  );
};

export default FundraiserListDataTable;
